import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface StokUrun {
  id: string;
  urun_adi: string;
  urun_kategorisi: string;
  stok_miktari: number;
  minimum_stok_seviyesi: number;
  birim: string;
  alis_fiyati: number;
  satis_fiyati: number;
  aktif: boolean;
  notlar: string;
  created_at: string;
}

export interface StokHareket {
  id: string;
  created_at: string;
  stok_urun_id: string;
  hareket_turu: string;
  miktar: number;
  birim_fiyat: number;
  aciklama: string;
  urun_adi?: string;
}

export function useStok() {
  const [urunler, setUrunler] = useState<StokUrun[]>([]);
  const [hareketler, setHareketler] = useState<StokHareket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: uData, error: uErr } = await supabase
        .from('sadem_stok_urunleri')
        .select('*')
        .eq('aktif', true)
        .order('urun_adi');

      if (uErr) throw uErr;

      const { data: hData, error: hErr } = await supabase
        .from('sadem_stok_hareketleri')
        .select('*, sadem_stok_urunleri(urun_adi)')
        .order('created_at', { ascending: false })
        .limit(30);

      if (hErr) throw hErr;

      setUrunler((uData || []).map((u: any) => ({
        id: u.id,
        urun_adi: u.urun_adi,
        urun_kategorisi: u.urun_kategorisi,
        stok_miktari: Number(u.stok_miktari),
        minimum_stok_seviyesi: Number(u.minimum_stok_seviyesi),
        birim: u.birim,
        alis_fiyati: Number(u.alis_fiyati),
        satis_fiyati: Number(u.satis_fiyati),
        aktif: u.aktif,
        notlar: u.notlar,
        created_at: u.created_at,
      })));

      setHareketler((hData || []).map((h: any) => ({
        id: h.id,
        created_at: h.created_at,
        stok_urun_id: h.stok_urun_id,
        hareket_turu: h.hareket_turu,
        miktar: Number(h.miktar),
        birim_fiyat: Number(h.birim_fiyat),
        aciklama: h.aciklama,
        urun_adi: h.sadem_stok_urunleri?.urun_adi,
      })));
    } catch (err: any) {
      setError(err.message || 'Stok verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { urunler, hareketler, loading, error, refetch: fetchData };
}

export async function stokHareketEkle(data: {
  stok_urun_id: string;
  hareket_turu: 'giris' | 'cikis';
  miktar: number;
  birim_fiyat?: number;
  aciklama?: string;
}) {
  const { data: urun, error: urunErr } = await supabase
    .from('sadem_stok_urunleri')
    .select('stok_miktari, satis_fiyati')
    .eq('id', data.stok_urun_id)
    .single();

  if (urunErr) throw urunErr;

  const yeniMiktar = data.hareket_turu === 'giris'
    ? Number(urun.stok_miktari) + data.miktar
    : Math.max(0, Number(urun.stok_miktari) - data.miktar);

  // Create hareket
  const { error: hErr } = await supabase.from('sadem_stok_hareketleri').insert({
    stok_urun_id: data.stok_urun_id,
    hareket_turu: data.hareket_turu,
    miktar: data.miktar,
    birim_fiyat: data.birim_fiyat || Number(urun.satis_fiyati),
    aciklama: data.aciklama || '',
  });

  if (hErr) throw hErr;

  // Update stok
  const { error: uErr } = await supabase.from('sadem_stok_urunleri')
    .update({ stok_miktari: yeniMiktar })
    .eq('id', data.stok_urun_id);

  if (uErr) throw uErr;
}